// Dependencies: @coreui/react @coreui/icons-react @coreui/icons
// npm install @coreui/react @coreui/icons-react @coreui/icons

import { useState } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CFormCheck,
  CRow,
  CCol,
  CButton,
  CAlert,
  CBadge,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle, cilWarning } from "@coreui/icons";

// Mock logged-in seller data (in real app, comes from auth context/store)
const loggedInSeller = {
  fullName: "Rajesh Kumar",
  email: "rajesh.kumar@steelco.in",
  phone: "+91 98765 43210",
  company: "SteelCo Industries Pvt. Ltd.",
};

const initialForm = {
  categoryName: "",
  subCategoryName: "",
  metalCode: "",
  description: "",
  approximateVolumeListing: [],
  additionalInformation: "",
  fullName: loggedInSeller.fullName,
  email: loggedInSeller.email,
  phone: loggedInSeller.phone,
  company: loggedInSeller.company,
};

export default function CategoryRequestModal({ visible, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.categoryName.trim()) errs.categoryName = "Category name is required.";
    if (!form.metalCode.trim()) errs.metalCode = "Metal code is required.";
    if (!form.description.trim()) errs.description = "Description is required.";
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    console.log("Category Request Submitted:", form);
    setSubmitted(true);
  };

  const handleClose = () => {
    setForm(initialForm);
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  return (
    <>
      <style>{`
        /* Cap modal height and enable scroll on body */
        .category-modal .modal-dialog {
          max-height: 90vh;
          margin: 1.5rem auto;
        }
        .category-modal .modal-content {
          max-height: 85vh;
          display: flex;
          flex-direction: column;
        }
        .category-modal .modal-body {
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1 1 auto;
          padding: 1rem 1.25rem;
        }
        /* Tighter padding on small screens */
        @media (max-width: 576px) {
          .category-modal .modal-dialog {
            margin: 0.5rem;
            max-height: 98vh;
          }
          .category-modal .modal-content {
            max-height: 97vh;
          }
          .category-modal .modal-body {
            padding: 0.75rem;
          }
          .category-modal .modal-header,
          .category-modal .modal-footer {
            padding: 0.75rem;
          }
          /* Stack columns on mobile */
          .category-modal .row > [class*="col-md"] {
            margin-bottom: 0.75rem;
          }
        }
      `}</style>

      <CModal
        visible={visible}
        onClose={handleClose}
        size="lg"
        alignment="center"
        backdrop="static"
        scrollable
        className="category-modal"
      >
        <CModalHeader>
          <CModalTitle className="d-flex align-items-center flex-wrap gap-2" style={{ fontSize: "1rem" }}>
            Request New Category
            <CBadge color="warning" style={{ fontSize: "10px" }}>
              Pending Review
            </CBadge>
          </CModalTitle>
        </CModalHeader>

        <CModalBody>
          {submitted ? (
            <CAlert color="success" className="d-flex align-items-start gap-3 mb-0">
              <CIcon icon={cilCheckCircle} size="xl" className="mt-1 flex-shrink-0" />
              <div>
                <strong>Request Submitted Successfully!</strong>
                <p className="mb-0 mt-1 text-body-secondary" style={{ fontSize: "13px" }}>
                  Your new category request has been received and is pending admin review. We'll notify
                  you at <strong>{form.email}</strong> once a decision is made.
                </p>
              </div>
            </CAlert>
          ) : (
            <CForm>
              {/* ── Category Details ── */}
              <p
                className="text-body-secondary fw-semibold text-uppercase mb-2"
                style={{ fontSize: "11px", letterSpacing: "1px" }}
              >
                Category Details
              </p>

              <CRow className="mb-2 g-2">
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="categoryName" className="mb-1">
                    Category Name <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    id="categoryName"
                    name="categoryName"
                    placeholder="e.g. Ferrous Metals"
                    value={form.categoryName}
                    onChange={handleChange}
                    invalid={!!errors.categoryName}
                    feedbackInvalid={errors.categoryName}
                    size="sm"
                  />
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="subCategoryName" className="mb-1">
                    Sub-Category Name
                  </CFormLabel>
                  <CFormInput
                    id="subCategoryName"
                    name="subCategoryName"
                    placeholder="e.g. Stainless Steel"
                    value={form.subCategoryName}
                    onChange={handleChange}
                    size="sm"
                  />
                </CCol>
              </CRow>

              <CRow className="mb-2 g-2">
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="metalCode" className="mb-1">
                    Metal Code <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    id="metalCode"
                    name="metalCode"
                    placeholder="e.g. SS-304, AL-6061"
                    value={form.metalCode}
                    onChange={handleChange}
                    invalid={!!errors.metalCode}
                    feedbackInvalid={errors.metalCode}
                    size="sm"
                  />
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel className="mb-1">Approximate Volume Listing / Month</CFormLabel>
                  <div className="d-flex gap-3 flex-wrap mt-1">
                    {[15, 20, 25].map((val) => (
                      <CFormCheck
                        key={val}
                        id={`volume-${val}`}
                        label={`${val}`}
                        checked={form.approximateVolumeListing.includes(val)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...form.approximateVolumeListing, val]
                            : form.approximateVolumeListing.filter((v) => v !== val);
                          setForm((prev) => ({ ...prev, approximateVolumeListing: updated }));
                        }}
                      />
                    ))}
                  </div>
                </CCol>
              </CRow>

              <div className="mb-2">
                <CFormLabel htmlFor="description" className="mb-1">
                  Description <span className="text-danger">*</span>
                </CFormLabel>
                <CFormTextarea
                  id="description"
                  name="description"
                  rows={2}
                  placeholder="Describe the category — material properties, typical use cases, applicable standards..."
                  value={form.description}
                  onChange={handleChange}
                  invalid={!!errors.description}
                  feedbackInvalid={errors.description}
                />
              </div>

              <div className="mb-3">
                <CFormLabel htmlFor="additionalInformation" className="mb-1">
                  Additional Information
                </CFormLabel>
                <CFormTextarea
                  id="additionalInformation"
                  name="additionalInformation"
                  rows={2}
                  placeholder="Any references, or special requirements..."
                  value={form.additionalInformation}
                  onChange={handleChange}
                />
              </div>

              {/* ── Contact Details ── */}
              <div className="border-top pt-3">
                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
                  <p
                    className="text-body-secondary fw-semibold text-uppercase mb-0"
                    style={{ fontSize: "11px", letterSpacing: "1px" }}
                  >
                    Contact Details
                  </p>
                  <CBadge color="info" className="fw-normal" style={{ fontSize: "10px" }}>
                    Pre-filled from your profile
                  </CBadge>
                </div>

                <CAlert color="light" className="py-2 px-3 mb-2 border d-flex align-items-center gap-2">
                  <CIcon icon={cilWarning} size="sm" className="text-warning flex-shrink-0" />
                  <small className="text-body-secondary" style={{ fontSize: "12px" }}>
                    Auto-filled from your seller profile. Update your profile if any info has changed.
                  </small>
                </CAlert>

                <CRow className="mb-2 g-2">
                  <CCol xs={12} md={6}>
                    <CFormLabel htmlFor="fullName" className="mb-1">Full Name</CFormLabel>
                    <CFormInput
                      id="fullName"
                      name="fullName"
                      value={form.fullName}
                      readOnly
                      className="bg-body-tertiary"
                      size="sm"
                    />
                  </CCol>
                  <CCol xs={12} md={6}>
                    <CFormLabel htmlFor="company" className="mb-1">Company / Organization</CFormLabel>
                    <CFormInput
                      id="company"
                      name="company"
                      value={form.company}
                      readOnly
                      className="bg-body-tertiary"
                      size="sm"
                    />
                  </CCol>
                </CRow>

                <CRow className="g-2">
                  <CCol xs={12} md={6}>
                    <CFormLabel htmlFor="email" className="mb-1">Email Address</CFormLabel>
                    <CFormInput
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      readOnly
                      className="bg-body-tertiary"
                      size="sm"
                    />
                  </CCol>
                  <CCol xs={12} md={6}>
                    <CFormLabel htmlFor="phone" className="mb-1">Phone Number</CFormLabel>
                    <CFormInput
                      id="phone"
                      name="phone"
                      value={form.phone}
                      readOnly
                      className="bg-body-tertiary"
                      size="sm"
                    />
                  </CCol>
                </CRow>
              </div>
            </CForm>
          )}
        </CModalBody>

        <CModalFooter className="py-2">
          <CButton color="secondary" variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </CButton>
          {!submitted ? (
            <CButton color="primary" size="sm" onClick={handleSubmit}>
              Submit Request
            </CButton>
          ) : (
            <CButton color="primary" variant="outline" size="sm" onClick={handleClose}>
              Close
            </CButton>
          )}
        </CModalFooter>
      </CModal>
    </>
  );
}